import { useState } from "react";
import qrCodeExports from "react-qr-code";
import { MODELS, useModel } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import { ROUTES } from "../../../routes.js";
import Button from "../../../design-system/primitives/components/Button/Button";
import Dialog from "../../../design-system/primitives/components/Dialog/Dialog";
import CopyToClipboard from "../../../design-system/primitives/components/CopyToClipboard/CopyToClipboard";
import Text from "../../../design-system/primitives/components/Text/Text";
import { mode } from "../../../design-system/primitives/modes.css";
import styles from "./InviteStudentsButton.css";

// `react-qr-code` is Babel-transpiled CommonJS, and Vite's dev server binds a
// default import of such a package to the whole `exports` object instead of
// to `exports.default` — React then rejects that object as an invalid
// element type. The production build interops correctly. react-qr-code's own
// types assume the correct case, so this genuinely doesn't typecheck without
// lying about it — hence the suppression, not a cast.
const QRCode =
  // @ts-expect-error -- see comment above; `.default` isn't in react-qr-code's
  // types, but exists on this value at dev-server runtime.
  qrCodeExports.default ?? qrCodeExports;

type InviteStudentsButtonProps = {
  workshopId: WorkshopId;
};

/**
 * The "+ Einladen" action for the students tab: opens a QR-code + copyable
 * link dialog. Matches Figma's generic Dialog anatomy (description, then a
 * content-slot, then a footer that stacks the copy row above a close button).
 */
const InviteStudentsButton = ({ workshopId }: InviteStudentsButtonProps) => {
  const studentInvite = useModel(MODELS.WORKSHOP_STUDENT_INVITE(workshopId));
  const [open, setOpen] = useState(false);

  if (!studentInvite) {
    return (
      <Text variant="body" color="neutral-100">
        Kein Einladungslink gefunden. Kontaktiere unseren Support.
      </Text>
    );
  }

  const inviteLink = `${window.location.origin}${ROUTES.registerPage.getUrl(studentInvite.secret)}`;

  return (
    <>
      <Button
        title="Einladen"
        iconLeft="plus"
        color="accent"
        size="large"
        onClick={() => setOpen(true)}
      />
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Schüler*innen via QR-Code einladen"
        description="Schaue dir die Digital Sparks Lernreise an, bereite alles vor und lade sobald du soweit bist, deine Schüler*innen ein!"
        closeAriaLabel="Schließen"
        contentSlot={
          <div className={styles.qrCode}>
            <QRCode
              value={inviteLink}
              size={256}
              level="H"
              fgColor={mode.neutral["0"]}
              bgColor={mode.neutral["800"]}
            />
          </div>
        }
        footer={
          <>
            <CopyToClipboard
              value={inviteLink}
              label="Einladungslink kopieren"
            />
            <Button
              title="QR-Code schließen"
              color="accent"
              size="large"
              onClick={() => setOpen(false)}
              fullWidth
            />
          </>
        }
      />
    </>
  );
};

export default InviteStudentsButton;
